import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  FormBrandOverride,
  FormConsentMode,
  FormDeliveryMode,
  FormFieldDefinition,
  FormFieldType,
  FormPathType,
  FormStatus,
  ResolvedFormBrand,
  FormConsentSnapshot,
} from '@engage/types';
import { apiClient } from './client';

// ─── REST types ──────────────────────────────────────────────────────────────

export interface Form {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  path_type: FormPathType;
  status: FormStatus;
  slug: string;
  current_draft_version_id: string | null;
  current_published_version_id: string | null;
  brand_override: FormBrandOverride | null;
  consent_mode: FormConsentMode | null;
  consent_text: string | null;
  gated_document_id: string | null;
  gated_delivery_mode: FormDeliveryMode | null;
  gated_document_ttl_seconds: number | null;
  gated_email_subject: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  /** Total completed submissions — present on the list endpoint only. */
  submission_count?: number;
}

export interface FormVersion {
  id: string;
  form_id: string;
  version_number: number;
  fields: FormFieldDefinition[];
  brand_snapshot: ResolvedFormBrand | Record<string, never>;
  consent_snapshot: FormConsentSnapshot | null;
  published_at: string | null;
  created_at: string;
}

export interface FormDetail {
  form: Form;
  current_version: FormVersion | null;
  published_version: FormVersion | null;
}

export interface NewFormInput {
  name: string;
  path_type: FormPathType;
  description?: string;
  slug?: string;
}

export interface FormPatchInput {
  name?: string;
  description?: string | null;
  slug?: string;
  brand_override?: FormBrandOverride | null;
  consent_mode?: FormConsentMode | null;
  consent_text?: string | null;
  gated_document_id?: string | null;
  gated_delivery_mode?: FormDeliveryMode | null;
  gated_document_ttl_seconds?: number | null;
  gated_email_subject?: string | null;
}

export interface CandidateFieldKey {
  key: string;
  label: string;
  type: 'STRING' | 'NUMBER' | 'ENUM';
  bounds_apply: boolean;
  /** The question linking the field sets up, with JobTetris's own choices (JT1-1924). */
  field_type?: FormFieldType;
  options?: { value: string; label: string }[];
  hint?: string;
}

const keys = {
  list: (filters?: { status?: FormStatus; path_type?: FormPathType; search?: string }) =>
    ['forms', filters ?? {}] as const,
  detail: (id: string) => ['forms', id] as const,
  candidateFieldKeys: () => ['forms', 'candidate-field-keys'] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function useForms(filters?: { status?: FormStatus; path_type?: FormPathType; search?: string }) {
  return useQuery({
    queryKey: keys.list(filters),
    queryFn: async () => {
      const r = await apiClient.get<{ data: Form[] }>('/forms', { params: filters });
      return r.data.data;
    },
  });
}

export interface FormSubmissionRow {
  id: string;
  submitted_at: string;
  source: string;
  path_type: FormPathType;
  field_values: Record<string, unknown>;
  contact_id: string | null;
}

/** Recent completed submissions for a form — backs the in-app review surface. */
export function useFormSubmissions(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['forms', id, 'submissions'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: FormSubmissionRow[] }>(`/forms/${id}/submissions`);
      return r.data.data;
    },
    enabled: !!id && enabled,
  });
}

export function useForm(id: string | undefined) {
  return useQuery({
    queryKey: id ? keys.detail(id) : ['forms', 'none'],
    queryFn: async () => {
      const r = await apiClient.get<{ data: FormDetail }>(`/forms/${id}`);
      return r.data.data;
    },
    enabled: !!id,
  });
}

export function useCreateForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewFormInput) => {
      const r = await apiClient.post<{ data: Form }>('/forms', input);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms'] }),
  });
}

export function useUpdateForm(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: FormPatchInput) => {
      const r = await apiClient.patch<{ data: Form }>(`/forms/${id}`, patch);
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['forms'] });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useSaveDraft(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { fields: FormFieldDefinition[] }) => {
      const r = await apiClient.put<{ data: FormVersion }>(`/forms/${id}/draft`, body);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.detail(id) }),
  });
}

export function usePublishForm(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await apiClient.post<{ data: FormVersion }>(`/forms/${id}/publish`);
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['forms'] });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useArchiveForm(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.post(`/forms/${id}/archive`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forms'] }),
  });
}

export function useCandidateFieldKeys() {
  return useQuery({
    queryKey: keys.candidateFieldKeys(),
    queryFn: async () => {
      const r = await apiClient.get<{ data: CandidateFieldKey[] }>('/forms/candidate-field-keys');
      return r.data.data;
    },
    staleTime: 1000 * 60 * 5, // the agency's custom fields can change (JT1-1924)
  });
}
